import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useUserSettingsContext } from '../context/UserSettingsContext';
import LoadingScreen from '../components/LoadingScreen';
import AiPositionGlMappingModal from '../components/AiPositionGlMappingModal';
import { ConfigPageShell, ScopeSelect } from '../components/ConfigPages';
import { InlineSelect } from '../components/InlineTable';
import { getPositions, invalidatePositionsCache } from '../utils/positionsApi';
import { getDepartmentsByOrgId, clearDepartmentCache, getDepartmentCoaMappings, getLaborCoaMappings, bulkUpsertLaborCoaMappings } from '../utils/departmentApi';
import { getTypeClassifications, getChartOfAccounts } from '../utils/api';
import { getLocationsByOrgId } from '../utils/locationApi';
import { getRevenueBasisGroups } from '../utils/revenueBasisApi';
import PositionsTab from './positionMap/PositionsTab';
import RevenueBasisTab from './positionMap/RevenueBasisTab';
import LaborGlTab from './positionMap/LaborGlTab';
import DepartmentModal from './positionMap/DepartmentModal';
import RevenueBasisModal from './positionMap/RevenueBasisModal';

const fmt = (n) => Number(n || 0).toLocaleString();

/**
 * Position map — how payroll positions roll up to departments, classifications
 * and GL accounts. This file owns the data + the shell; each tab and modal
 * lives in ./positionMap/.
 */
const PositionDepartmentMappingPage = () => {
  const { currentOrganization } = useUserSettingsContext();
  const orgId = currentOrganization?.id;

  const [positions, setPositions] = useState([]); // includes soft-deleted rows
  const [departments, setDepartments] = useState([]);
  const [typeClassifications, setTypeClassifications] = useState([]);
  const [revenueBasisGroups, setRevenueBasisGroups] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useState('positions'); // positions | revenueBasis | laborGl
  const [positionsStatus, setPositionsStatus] = useState('all');
  const [laborStatus, setLaborStatus] = useState('all');

  // ONE location for the whole page: Labor GL tab scope + Department modal COA mapping.
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [chartOfAccounts, setChartOfAccounts] = useState([]);
  const [departmentCoaMappings, setDepartmentCoaMappings] = useState([]);
  const [laborCoaMappings, setLaborCoaMappings] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);

  const [departmentModal, setDepartmentModal] = useState(null); // null | 'list' | 'create'
  const [revenueBasisModal, setRevenueBasisModal] = useState(null); // null | { group: null | {...} }
  const [showAiModal, setShowAiModal] = useState(false);

  // Global lookup (not org specific)
  useEffect(() => {
    getTypeClassifications().then((d) => setTypeClassifications(d || [])).catch((err) => console.error('Error fetching type classifications:', err));
  }, []);

  const reloadPositions = useCallback(async () => {
    if (!orgId) return;
    invalidatePositionsCache();
    const data = await getPositions({ orgId, includeDeleted: true });
    setPositions(data || []);
  }, [orgId]);

  const reloadDepartments = useCallback(async () => {
    if (!orgId) return;
    clearDepartmentCache();
    const data = await getDepartmentsByOrgId(orgId);
    setDepartments(data || []);
  }, [orgId]);

  // Org-level data. Reset the location on org change (a location id never crosses orgs).
  useEffect(() => {
    let cancelled = false;
    setSelectedLocationId(null);
    if (!orgId) { setLoading(false); setPositions([]); setDepartments([]); setRevenueBasisGroups([]); setLocations([]); return undefined; }
    setLoading(true);
    invalidatePositionsCache();
    Promise.all([
      getPositions({ orgId, includeDeleted: true }),
      getDepartmentsByOrgId(orgId),
      getRevenueBasisGroups({ orgId }),
      getLocationsByOrgId(orgId).catch((err) => { console.error('Error fetching locations:', err); return []; }),
    ]).then(([p, d, g, l]) => {
      if (cancelled) return;
      setPositions(p || []); setDepartments(d || []); setRevenueBasisGroups(g || []); setLocations(l || []);
    }).catch((err) => {
      if (cancelled) return;
      console.error('Error fetching position map data:', err);
      toast.error('Could not load positions and departments — reload the page to try again');
      setPositions([]); setDepartments([]); setRevenueBasisGroups([]);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orgId]);

  // Location-level data: COA + department→COA + position→GL mappings.
  useEffect(() => {
    let cancelled = false;
    if (!orgId || !selectedLocationId) { setChartOfAccounts([]); setDepartmentCoaMappings([]); setLaborCoaMappings([]); return undefined; }
    setLocationLoading(true);
    Promise.all([
      getChartOfAccounts(orgId, selectedLocationId),
      getDepartmentCoaMappings(orgId, selectedLocationId),
      getLaborCoaMappings(orgId, selectedLocationId),
    ]).then(([coa, deptMaps, laborMaps]) => {
      if (cancelled) return;
      setChartOfAccounts(coa || []); setDepartmentCoaMappings(deptMaps || []); setLaborCoaMappings(laborMaps || []);
    }).catch((err) => {
      if (cancelled) return;
      console.error('Error fetching location data:', err);
      toast.error('Could not load the chart of accounts for this location');
    }).finally(() => { if (!cancelled) setLocationLoading(false); });
    return () => { cancelled = true; };
  }, [orgId, selectedLocationId]);

  // ── Derived ────────────────────────────────────────────────────────────────
  const livePositions = useMemo(() => positions.filter((p) => !p.isDeleted), [positions]);
  const departmentOptions = useMemo(() => departments.map((d) => ({ value: d.id, label: d.name })), [departments]);
  const classificationOptions = useMemo(() => typeClassifications.map((t) => ({ value: t.id, label: t.name })), [typeClassifications]);
  const revenueBasisOptions = useMemo(() => revenueBasisGroups.map((g) => ({ value: g.id, label: g.name, hint: g.code })), [revenueBasisGroups]);
  const locationOptions = useMemo(() => (locations || []).map((l) => ({ value: l.id, label: l.name })), [locations]);
  const coaOptions = useMemo(() => chartOfAccounts.map((a) => ({ value: a.id, label: `${a.accountNumber} - ${a.accountName}` })), [chartOfAccounts]);
  const laborAccounts = useMemo(() => chartOfAccounts.filter((a) => a.accountType?.toLowerCase() === 'expense'), [chartOfAccounts]);
  const laborAccountOptions = useMemo(() => laborAccounts.map((a) => ({ value: a.id, label: `${a.accountNumber} - ${a.accountName}`, hint: a.department || undefined })), [laborAccounts]);
  const selectedLocationName = locationOptions.find((o) => o.value === selectedLocationId)?.label;

  // "lands in" under the Department cell: the department's COA at the current
  // location if known, else the labor dashboard line for that department.
  const landsInByDepartmentId = useCallback((departmentId) => {
    if (!departmentId) return null;
    const dept = departments.find((d) => d.id === departmentId);
    if (!dept) return null;
    const m = departmentCoaMappings.find((x) => x.departmentId === departmentId);
    const acct = m ? chartOfAccounts.find((a) => a.id === m.chartOfAccountId) : null;
    if (acct) return `GL ${acct.accountNumber} · ${acct.accountName}`;
    // No GL account at this location — the department name itself is the
    // dashboard line, and it's already in the cell; don't echo it.
    return null;
  }, [departments, departmentCoaMappings, chartOfAccounts]);

  const posCounts = useMemo(() => {
    const total = livePositions.length;
    const withDept = livePositions.filter((p) => p.departmentId).length;
    const withBoth = livePositions.filter((p) => p.departmentId && p.typeClassificationId).length;
    return { total, withDept, withBoth, unmapped: total - withBoth };
  }, [livePositions]);

  const laborCounts = useMemo(() => {
    const byPos = new Set(laborCoaMappings.filter((m) => m.chartOfAccountId != null).map((m) => m.positionId));
    const mapped = livePositions.filter((p) => byPos.has(p.id)).length;
    return { total: livePositions.length, mapped, unmapped: livePositions.length - mapped };
  }, [livePositions, laborCoaMappings]);

  const basisClassificationCount = useMemo(() => {
    const ids = new Set();
    revenueBasisGroups.forEach((g) => (g.members || []).forEach((m) => ids.add(m.typeClassificationId)));
    return ids.size;
  }, [revenueBasisGroups]);

  const flow = useMemo(() => {
    if (activeTab === 'positions') {
      const stations = [
        { label: 'Positions', value: fmt(posCounts.total), sub: 'from the payroll feed' },
        { label: 'Department + classification', value: `${fmt(posCounts.withBoth)} mapped`, tone: posCounts.withBoth === posCounts.total && posCounts.total > 0 ? 'ok' : 'neutral', sub: `${fmt(posCounts.withDept)} with a department` },
      ];
      if (posCounts.unmapped > 0) stations.push({ label: 'Needs mapping', value: fmt(posCounts.unmapped), tone: 'warn', sub: 'no department or classification' });
      stations.push({ label: 'Lands in', value: 'Labor dashboard line', mono: false, sub: 'by department · classification' });
      return { stations, coverage: { mapped: posCounts.withBoth, total: posCounts.total, label: 'with department + classification' }, onUnmappedClick: () => setPositionsStatus('unmapped') };
    }
    if (activeTab === 'revenueBasis') {
      return {
        stations: [
          { label: 'Bases', value: fmt(revenueBasisGroups.length), sub: `${fmt(livePositions.filter((p) => p.revenueBasisGroupId).length)} positions use one` },
          { label: 'Classifications', value: fmt(basisClassificationCount), sub: 'revenue classifications referenced' },
          { label: 'Lands in', value: '% of revenue driver', mono: false, sub: 'labor cost ÷ this revenue' },
        ],
      };
    }
    if (!selectedLocationId) {
      return {
        stations: [
          { label: 'Positions', value: fmt(posCounts.total) },
          { label: 'GL account', value: '—', tone: 'muted', sub: 'choose a location' },
          { label: 'Lands in', value: 'Labor P&L line', mono: false },
        ],
      };
    }
    const stations = [
      { label: 'Positions', value: fmt(laborCounts.total), sub: selectedLocationName },
      { label: 'GL account', value: `${fmt(laborCounts.mapped)} mapped`, tone: laborCounts.mapped === laborCounts.total && laborCounts.total > 0 ? 'ok' : 'neutral', sub: `${fmt(laborAccounts.length)} expense accounts` },
    ];
    if (laborCounts.unmapped > 0) stations.push({ label: 'Needs account', value: fmt(laborCounts.unmapped), tone: 'warn', sub: 'not on the labor P&L' });
    stations.push({ label: 'Lands in', value: 'Labor P&L line', mono: false, sub: 'by GL account · department' });
    return { stations, coverage: { mapped: laborCounts.mapped, total: laborCounts.total, label: 'mapped to a GL account' }, onUnmappedClick: () => setLaborStatus('unmapped') };
  }, [activeTab, posCounts, laborCounts, revenueBasisGroups.length, basisClassificationCount, livePositions, selectedLocationId, selectedLocationName, laborAccounts.length]);

  // AI apply → bulk upsert, then replace mappings with the server list.
  const handleApplyAiMappings = async (mappings) => {
    if (!selectedLocationId || !orgId || mappings.length === 0) return;
    setShowAiModal(false);
    const n = mappings.length;
    const toastId = toast.loading(`Applying ${n} mapping${n !== 1 ? 's' : ''}…`);
    try {
      const result = await bulkUpsertLaborCoaMappings(orgId, selectedLocationId, mappings);
      if (result?.mappings) setLaborCoaMappings(result.mappings);
      toast.dismiss(toastId);
      toast.success(`Applied ${n} mapping${n !== 1 ? 's' : ''} (${result?.created || 0} new, ${result?.updated || 0} updated)`);
    } catch (err) {
      console.error('Error bulk applying mappings:', err);
      toast.dismiss(toastId);
      toast.error('Could not apply the mappings — nothing was changed. Try again.');
    }
  };

  const positionsWithDepartments = useMemo(
    () => livePositions.map((p) => ({ ...p, departmentName: departments.find((d) => d.id === p.departmentId)?.name || null })),
    [livePositions, departments],
  );

  const onRevenueBasisSaved = (saved) => {
    setRevenueBasisGroups((prev) => {
      const next = prev.some((g) => g.id === saved.id) ? prev.map((g) => (g.id === saved.id ? saved : g)) : [...prev, saved];
      return next.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    });
  };

  if (loading) return <LoadingScreen message="Loading position map" />;
  if (!currentOrganization) return <div className="p-4">Please select an organization first.</div>;

  const tabs = [
    { key: 'positions', label: 'Positions', count: fmt(posCounts.total) },
    { key: 'revenueBasis', label: 'Revenue basis' },
    { key: 'laborGl', label: 'Labor GL', count: selectedLocationId ? fmt(laborCounts.mapped) : undefined },
  ];

  return (
    <ConfigPageShell
      title="Position map"
      subtitle="How payroll positions roll up to departments, classifications and GL accounts."
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      scope={activeTab === 'laborGl' ? (
        <ScopeSelect>
          <InlineSelect rowActive value={selectedLocationId} options={locationOptions} onChange={(v) => setSelectedLocationId(v ?? null)} placeholder="Choose a location…" minWidth={220} ariaLabel="Location" />
        </ScopeSelect>
      ) : null}
      flow={flow}
    >
      {activeTab === 'positions' && (
        <PositionsTab
          orgId={orgId}
          positions={positions}
          setPositions={setPositions}
          departments={departments}
          departmentOptions={departmentOptions}
          classificationOptions={classificationOptions}
          revenueBasisOptions={revenueBasisOptions}
          revenueBasisGroups={revenueBasisGroups}
          typeClassifications={typeClassifications}
          landsInByDepartmentId={landsInByDepartmentId}
          status={positionsStatus}
          setStatus={setPositionsStatus}
          onOpenDepartments={() => setDepartmentModal('list')}
          onAddDepartment={() => setDepartmentModal('create')}
        />
      )}
      {activeTab === 'revenueBasis' && (
        <RevenueBasisTab
          groups={revenueBasisGroups}
          setGroups={setRevenueBasisGroups}
          positions={positions}
          typeClassifications={typeClassifications}
          onCreate={() => setRevenueBasisModal({ group: null })}
          onEdit={(group) => setRevenueBasisModal({ group })}
          reloadPositions={reloadPositions}
        />
      )}
      {activeTab === 'laborGl' && (
        <LaborGlTab
          orgId={orgId}
          locationId={selectedLocationId}
          locationName={selectedLocationName}
          loading={locationLoading}
          positions={positions}
          departments={departments}
          accounts={laborAccounts}
          accountOptions={laborAccountOptions}
          mappings={laborCoaMappings}
          setMappings={setLaborCoaMappings}
          status={laborStatus}
          setStatus={setLaborStatus}
          onOpenAi={() => setShowAiModal(true)}
        />
      )}

      <DepartmentModal
        open={!!departmentModal}
        initialMode={departmentModal || 'list'}
        onClose={() => setDepartmentModal(null)}
        orgId={orgId}
        departments={departments}
        setDepartments={setDepartments}
        positions={positions}
        locationOptions={locationOptions}
        locationId={selectedLocationId}
        setLocationId={setSelectedLocationId}
        coaOptions={coaOptions}
        coaMappings={departmentCoaMappings}
        setCoaMappings={setDepartmentCoaMappings}
        onDepartmentsChanged={async () => { await Promise.all([reloadPositions(), reloadDepartments()]); }}
      />

      <RevenueBasisModal
        open={!!revenueBasisModal}
        group={revenueBasisModal?.group || null}
        orgId={orgId}
        classificationOptions={classificationOptions}
        onClose={() => setRevenueBasisModal(null)}
        onSaved={onRevenueBasisSaved}
      />

      <AiPositionGlMappingModal
        isOpen={showAiModal}
        onClose={() => setShowAiModal(false)}
        positions={positionsWithDepartments}
        glAccounts={laborAccounts}
        existingMappings={laborCoaMappings}
        orgId={orgId}
        locationId={selectedLocationId}
        onApplyMappings={handleApplyAiMappings}
      />
    </ConfigPageShell>
  );
};

export default PositionDepartmentMappingPage;
